// Incident log + fault review workflow. Logging an incident is separate
// from classifying it: an admin (or, from Phase 3, a driver) records what
// happened first, then an admin reviews it later to set fault and, if
// applicable, confirm an excess fund charge -- the two steps don't have to
// happen in the same sitting.
import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAppContext } from "@/contexts/AppContext";
import { canEditTab } from "@/lib/permissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, RefreshCw, DollarSign, Trash2, Camera, X, Mic } from "lucide-react";
import {
  InsuranceIncident,
  IncidentCategory,
  FaultClassification,
  IncidentStatus,
  IncidentThirdParty,
  IncidentPhoto,
  InsuranceClaim,
  DamageDiagramView,
  IncidentDamageMarker,
  fetchIncidents,
  createIncident,
  updateIncident,
  deleteIncident,
  saveThirdParty,
  deleteThirdParty,
  fetchIncidentThirdParties,
  addIncidentPhoto,
  fetchIncidentPhotos,
  fetchClaims,
  fetchIncidentDamageMarkers,
  replaceIncidentDamageMarkersForView,
} from "@/lib/incidentReports";
import {
  DEFAULT_INSURANCE_EXCESS_SETTINGS,
  InsuranceExcessSettings,
  FundStatus,
  calculateFundStatus,
  loadDriverFundLedger,
  loadInsuranceExcessSettings,
} from "@/lib/insuranceExcess";
import { DAMAGE_DIAGRAM_VIEWS, resolveOverlayAssetKey, overlayPublicUrl } from "@/lib/damageDiagram";
import { loadVehicleTypes, VehicleTypeRow } from "@/lib/vehicleTypes";
import { formatDate } from "@/lib/dates";
import DamageMarkerOverlay, { DamageMarker } from "./DamageMarkerOverlay";
import IncidentResponsibilityPanel from "./IncidentResponsibilityPanel";

interface DriverOption {
  driver_name: string;
  insurance_start_date: string | null;
  insurance_last_claim_date: string | null;
}
interface VehicleOption {
  vehicle_number: string;
  registration: string | null;
  status: string | null;
  vehicle_type: string | null;
}

const CATEGORY_LABEL: Record<IncidentCategory, string> = {
  vehicle_collision: "Vehicle Collision",
  property_damage: "Property Damage",
  single_vehicle: "Single Vehicle",
  other: "Other",
};
const FAULT_LABEL: Record<FaultClassification, string> = {
  not_at_fault: "Not At Fault",
  at_fault: "At Fault (Full Claim)",
  section2: "Section 2 (no cost to us)",
  section2_with_repair_cost: "Section 2 + Repair Cost",
  repair_internally: "Repair Internally (No Claim)",
};
// Short enough that the deduction's description stays under ~50 characters
// on the driver's commission summary.
const FAULT_SHORT: Record<FaultClassification, string> = {
  not_at_fault: "Not at fault",
  at_fault: "At fault",
  section2: "Section 2",
  section2_with_repair_cost: "Section 2 + repair",
  repair_internally: "Repair",
};
const STATUS_BADGE: Record<IncidentStatus, string> = {
  draft: "bg-gray-700 text-gray-200",
  submitted_for_review: "bg-yellow-800 text-yellow-200",
  reviewed: "bg-blue-800 text-blue-200",
  closed: "bg-green-800 text-green-200",
};

const emptyForm = {
  driver_name: "",
  vehicle_number: "",
  incident_category: "vehicle_collision" as IncidentCategory,
  incident_date: new Date().toISOString().slice(0, 10),
  incident_time: "",
  location_description: "",
  weather_conditions: "",
  road_conditions: "",
  narrative: "",
  police_involved: false,
  police_incident_number: "",
  witnesses_present: false,
  witness_details: "",
  injuries: false,
  injury_details: "",
  alcohol_drugs_involved: false,
  vehicle_location_now: "",
  own_vehicle_damage_description: "",
};
type IncidentForm = typeof emptyForm;

// Mirrors the driver-portal wizard's third-party shape (ReportIncident.tsx)
// -- same insurance_incident_third_parties columns, just entered in one shot
// here instead of across steps, since this is a retrospective admin entry
// rather than a live at-the-scene report.
interface ThirdPartyForm {
  key: string;
  other_driver_name: string;
  other_driver_phone: string;
  other_driver_licence: string;
  other_vehicle_rego: string;
  other_vehicle_make: string;
  other_vehicle_model: string;
  other_vehicle_damage_description: string;
  other_owner_name: string;
  other_owner_phone: string;
  other_insurer_name: string;
  other_claim_policy_number: string;
}
const emptyThirdParty = (): ThirdPartyForm => ({
  key: Math.random().toString(36).slice(2),
  other_driver_name: "",
  other_driver_phone: "",
  other_driver_licence: "",
  other_vehicle_rego: "",
  other_vehicle_make: "",
  other_vehicle_model: "",
  other_vehicle_damage_description: "",
  other_owner_name: "",
  other_owner_phone: "",
  other_insurer_name: "",
  other_claim_policy_number: "",
});
const thirdPartyHasContent = (p: ThirdPartyForm) => Object.entries(p).some(([k, v]) => k !== "key" && v.trim());
const thirdPartyToForm = (p: IncidentThirdParty): ThirdPartyForm => ({
  key: p.id,
  other_driver_name: p.other_driver_name || "",
  other_driver_phone: p.other_driver_phone || "",
  other_driver_licence: p.other_driver_licence || "",
  other_vehicle_rego: p.other_vehicle_rego || "",
  other_vehicle_make: p.other_vehicle_make || "",
  other_vehicle_model: p.other_vehicle_model || "",
  other_vehicle_damage_description: p.other_vehicle_damage_description || "",
  other_owner_name: p.other_owner_name || "",
  other_owner_phone: p.other_owner_phone || "",
  other_insurer_name: p.other_insurer_name || "",
  other_claim_policy_number: p.other_claim_policy_number || "",
});

interface StagedPhoto {
  key: string;
  file: File;
  previewUrl: string;
}

const publicPhotoUrl = (path: string) => supabase.storage.from("incident-reports").getPublicUrl(path).data.publicUrl;

interface InsuranceIncidentsTabProps {
  /** Set by InsuranceHub when the Claims tab's "Edit Incident / Diagram"
   * button jumps here -- opens that incident's detail view once loaded. */
  initialOpenIncidentId?: string | null;
  onInitialOpenConsumed?: () => void;
}

const InsuranceIncidentsTab: React.FC<InsuranceIncidentsTabProps> = ({ initialOpenIncidentId, onInitialOpenConsumed }) => {
  // A restricted user (see src/lib/permissions.ts, Settings -> Team
  // Access) only sees incidents/claims tied to their linked driver(s) OR
  // vehicle(s) (either match is enough -- an incident tied to "their"
  // vehicle but a different driver at the wheel is still relevant), read-
  // only unless explicitly given edit rights. Unrestricted (the default --
  // everyone today) is unaffected.
  const { effectivePermissions } = useAppContext();
  const canEdit = canEditTab(effectivePermissions, "insurance");
  const scopedDriverNames = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedDriverNames;
  const scopedVehicleNumbers = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedVehicleNumbers;
  const isInScope = (row: { driver_name?: string | null; vehicle_number?: string | null }): boolean => {
    if (!scopedDriverNames && !scopedVehicleNumbers) return true;
    if (row.driver_name && scopedDriverNames?.includes(row.driver_name)) return true;
    if (row.vehicle_number && scopedVehicleNumbers?.includes(row.vehicle_number)) return true;
    return false;
  };

  const [incidents, setIncidents] = useState<InsuranceIncident[]>([]);
  const [claims, setClaims] = useState<InsuranceClaim[]>([]);
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [vehicles, setVehicles] = useState<VehicleOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [newForm, setNewForm] = useState(emptyForm);
  const [newThirdParties, setNewThirdParties] = useState<ThirdPartyForm[]>([]);
  const [newPhotos, setNewPhotos] = useState<StagedPhoto[]>([]);

  const [selected, setSelected] = useState<InsuranceIncident | null>(null);
  // Every field an admin could have gotten wrong or found out more about
  // after the fact -- category, narrative, location, conditions, third-party
  // presence -- editable directly on the incident, not locked the moment
  // it's logged. Same shape/fields as the Log Incident form.
  const [editForm, setEditForm] = useState<IncidentForm>(emptyForm);
  const [savingDetails, setSavingDetails] = useState(false);
  // Existing third parties get their own editable buffer (keyed by their
  // real id) so every field is correctable, not just the diagram vehicle
  // type. New parties can be added to an already-logged incident too --
  // each gets its own draft + immediate Save, since (unlike the create
  // form) there's no single follow-up submit to batch them into.
  const [thirdPartyDrafts, setThirdPartyDrafts] = useState<Record<string, ThirdPartyForm>>({});
  const [savingThirdPartyId, setSavingThirdPartyId] = useState<string | null>(null);
  const [addedThirdParties, setAddedThirdParties] = useState<ThirdPartyForm[]>([]);
  const [faultClassification, setFaultClassification] = useState<FaultClassification | "">("");
  const [repairCost, setRepairCost] = useState("");
  const [repairerName, setRepairerName] = useState("");
  const [repairerAddress, setRepairerAddress] = useState("");
  const [repairerPhone, setRepairerPhone] = useState("");
  const [repairerEmail, setRepairerEmail] = useState("");
  const [repairEstimate, setRepairEstimate] = useState("");
  const [excessSettings, setExcessSettings] = useState<InsuranceExcessSettings>(DEFAULT_INSURANCE_EXCESS_SETTINGS);
  const [fundPreview, setFundPreview] = useState<FundStatus | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [selectedThirdParties, setSelectedThirdParties] = useState<IncidentThirdParty[]>([]);
  const [selectedPhotos, setSelectedPhotos] = useState<IncidentPhoto[]>([]);
  const [uploadingSelectedPhoto, setUploadingSelectedPhoto] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [overlayAssetKey, setOverlayAssetKey] = useState<string | null>(null);
  const [activeDiagramView, setActiveDiagramView] = useState<DamageDiagramView>("front");
  const [damageMarkers, setDamageMarkers] = useState<IncidentDamageMarker[]>([]);
  const [savingMarkers, setSavingMarkers] = useState(false);

  // Third-party vehicles get their own diagram too, once an admin picks a
  // body-type silhouette for them (same catalog as our own fleet). Keyed by
  // third_party.id so each party's view tab and marker-save target stay
  // independent of our own vehicle's and of each other.
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState<VehicleTypeRow[]>([]);
  const [thirdPartyDiagramView, setThirdPartyDiagramView] = useState<Record<string, DamageDiagramView>>({});
  const [savingThirdPartyType, setSavingThirdPartyType] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const [inc, claimsRes, driversRes, vehiclesRes] = await Promise.all([
      fetchIncidents(),
      fetchClaims(),
      supabase.from("drivers").select("driver_name, insurance_start_date, insurance_last_claim_date").eq("is_active", true).order("driver_name"),
      supabase.from("vehicles").select("vehicle_number, registration, status, vehicle_type").order("vehicle_number"),
    ]);
    const incidentById = new Map(inc.map((i) => [i.id, i]));
    setIncidents(inc.filter(isInScope));
    setClaims(claimsRes.filter((c) => {
      const parentIncident = incidentById.get(c.incident_id);
      return parentIncident ? isInScope(parentIncident) : false;
    }));
    setDrivers(scopedDriverNames ? (driversRes.data || []).filter((d) => scopedDriverNames.includes(d.driver_name)) : driversRes.data || []);
    // Deliberately unfiltered by status -- a vehicle involved in an incident
    // is often the one now sitting in maintenance because of it, and
    // openDetail() below needs its vehicle_type regardless of status to
    // resolve the damage diagram overlay. The "new incident" vehicle picker
    // filters to active-only itself, further down.
    setVehicles(scopedVehicleNumbers ? (vehiclesRes.data || []).filter((v) => scopedVehicleNumbers.includes(v.vehicle_number)) : vehiclesRes.data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    loadVehicleTypes().then(setVehicleTypeOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedDriverNames, scopedVehicleNumbers]);

  useEffect(() => {
    if (!initialOpenIncidentId || incidents.length === 0) return;
    const incident = incidents.find((i) => i.id === initialOpenIncidentId);
    if (incident) openDetail(incident);
    onInitialOpenConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOpenIncidentId, incidents]);

  // Revokes every staged photo's blob URL and resets the form back to
  // empty -- used by both the explicit Clear button and Cancel, so testing
  // (or just backing out of a half-filled entry) never leaves stale data
  // sitting in the dialog for next time it opens.
  const clearNewForm = () => {
    newPhotos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    setNewForm(emptyForm);
    setNewThirdParties([]);
    setNewPhotos([]);
  };

  const addThirdParty = () => setNewThirdParties((prev) => [...prev, emptyThirdParty()]);
  const removeThirdParty = (key: string) => setNewThirdParties((prev) => prev.filter((p) => p.key !== key));
  const updateThirdParty = (key: string, field: keyof ThirdPartyForm, value: string) =>
    setNewThirdParties((prev) => prev.map((p) => (p.key === key ? { ...p, [field]: value } : p)));

  const addPhotoFiles = (files: FileList | null) => {
    if (!files) return;
    const staged: StagedPhoto[] = Array.from(files).map((file) => ({
      key: Math.random().toString(36).slice(2),
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setNewPhotos((prev) => [...prev, ...staged]);
  };
  const removePhoto = (key: string) =>
    setNewPhotos((prev) => {
      const found = prev.find((p) => p.key === key);
      if (found) URL.revokeObjectURL(found.previewUrl);
      return prev.filter((p) => p.key !== key);
    });

  const addIncident = async () => {
    if (!canEdit) return;
    if (!newForm.driver_name || !newForm.incident_date || !newForm.incident_category) {
      toast({ title: "Error", description: "Driver, date and incident type are required", variant: "destructive" });
      return;
    }
    setSaving(true);
    // Tracked separately from the try block so the catch can tell whether
    // the incident record itself made it in before whatever failed --
    // re-running this whole function on retry would otherwise create a
    // second, duplicate incident on top of the one that already saved.
    let created: InsuranceIncident | null = null;
    try {
      created = await createIncident({
        ...newForm,
        vehicle_number: newForm.vehicle_number || null,
        incident_time: newForm.incident_time || null,
        reported_by: "admin",
        status: "submitted_for_review",
      });
      for (const p of newThirdParties) {
        if (!thirdPartyHasContent(p)) continue;
        const { key, ...fields } = p;
        await saveThirdParty({ incident_id: created.id, party_type: "vehicle", ...fields });
      }
      for (let i = 0; i < newPhotos.length; i++) {
        const { file } = newPhotos[i];
        const path = `incidents/${created.id}/admin_${Date.now()}_${i}.jpg`;
        const { error: uploadError } = await supabase.storage.from("incident-reports").upload(path, file, {
          contentType: file.type || "image/jpeg",
          upsert: true,
        });
        if (uploadError) throw uploadError;
        await addIncidentPhoto({
          incident_id: created.id,
          category: "scene",
          storage_path: path,
          latitude: null,
          longitude: null,
          taken_at: new Date().toISOString(),
          uploaded_by: "admin",
        });
      }
      toast({ title: "Logged", description: "Incident recorded." });
      setShowAdd(false);
      clearNewForm();
      load();
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
      if (created) {
        // The incident itself saved -- whatever failed after that (a third
        // party, a photo) can be retried from its detail view instead of
        // resubmitting this form, which would create a duplicate incident.
        setShowAdd(false);
        clearNewForm();
        await load();
        openDetail(created);
      }
    }
    setSaving(false);
  };

  const openDetail = async (incident: InsuranceIncident) => {
    setSelected(incident);
    setEditForm({
      driver_name: incident.driver_name || "",
      vehicle_number: incident.vehicle_number || "",
      incident_category: incident.incident_category,
      incident_date: incident.incident_date,
      incident_time: incident.incident_time || "",
      location_description: incident.location_description || "",
      weather_conditions: incident.weather_conditions || "",
      road_conditions: incident.road_conditions || "",
      narrative: incident.narrative || "",
      police_involved: incident.police_involved,
      police_incident_number: incident.police_incident_number || "",
      witnesses_present: incident.witnesses_present,
      witness_details: incident.witness_details || "",
      injuries: incident.injuries,
      injury_details: incident.injury_details || "",
      alcohol_drugs_involved: incident.alcohol_drugs_involved,
      vehicle_location_now: incident.vehicle_location_now || "",
      own_vehicle_damage_description: incident.own_vehicle_damage_description || "",
    });
    setAddedThirdParties([]);
    setFaultClassification(incident.fault_classification || "");
    setRepairCost(incident.repair_cost != null ? String(incident.repair_cost) : "");
    setRepairerName(incident.repairer_name || "");
    setRepairerAddress(incident.repairer_address || "");
    setRepairerPhone(incident.repairer_phone || "");
    setRepairerEmail(incident.repairer_email || "");
    setRepairEstimate(incident.repair_estimate != null ? String(incident.repair_estimate) : "");
    setFundPreview(null);
    setSelectedThirdParties([]);
    setSelectedPhotos([]);
    setDamageMarkers([]);
    setOverlayAssetKey(null);
    setActiveDiagramView("front");
    const vehicle = vehicles.find((v) => v.vehicle_number === incident.vehicle_number);
    const [parties, photos, markers, assetKey] = await Promise.all([
      fetchIncidentThirdParties(incident.id),
      fetchIncidentPhotos(incident.id),
      fetchIncidentDamageMarkers(incident.id),
      resolveOverlayAssetKey(vehicle?.vehicle_type ?? null),
    ]);
    setSelectedThirdParties(parties);
    setThirdPartyDrafts(Object.fromEntries(parties.map((p) => [p.id, thirdPartyToForm(p)])));
    setSelectedPhotos(photos);
    setDamageMarkers(markers);
    setOverlayAssetKey(assetKey);
  };

  const reloadSelectedThirdParties = async () => {
    if (!selected) return;
    const parties = await fetchIncidentThirdParties(selected.id);
    setSelectedThirdParties(parties);
    setThirdPartyDrafts(Object.fromEntries(parties.map((p) => [p.id, thirdPartyToForm(p)])));
  };

  // Saves every core incident field -- category, narrative, location,
  // conditions, the police/witness/injury detail text -- separately from
  // Fault Classification below, since reviewing fault is a distinct step
  // from correcting or adding to what was originally logged.
  const saveDetails = async () => {
    if (!selected || !canEdit) return;
    setSavingDetails(true);
    try {
      const patch: Partial<InsuranceIncident> = {
        vehicle_number: editForm.vehicle_number || null,
        incident_category: editForm.incident_category,
        incident_date: editForm.incident_date,
        incident_time: editForm.incident_time || null,
        location_description: editForm.location_description || null,
        weather_conditions: editForm.weather_conditions || null,
        road_conditions: editForm.road_conditions || null,
        narrative: editForm.narrative || null,
        police_involved: editForm.police_involved,
        police_incident_number: editForm.police_involved ? editForm.police_incident_number || null : null,
        witnesses_present: editForm.witnesses_present,
        witness_details: editForm.witnesses_present ? editForm.witness_details || null : null,
        injuries: editForm.injuries,
        injury_details: editForm.injuries ? editForm.injury_details || null : null,
        alcohol_drugs_involved: editForm.alcohol_drugs_involved,
        vehicle_location_now: editForm.vehicle_location_now || null,
        own_vehicle_damage_description: editForm.own_vehicle_damage_description || null,
      };
      await updateIncident(selected.id, patch);
      toast({ title: "Saved", description: "Incident details updated." });
      setSelected((prev) => (prev ? { ...prev, ...patch } : prev));
      // The damage diagram silhouette is picked from the vehicle's type, so
      // correcting which vehicle this incident belongs to needs to re-pick it.
      const newVehicle = vehicles.find((v) => v.vehicle_number === editForm.vehicle_number);
      setOverlayAssetKey(await resolveOverlayAssetKey(newVehicle?.vehicle_type ?? null));
      load();
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setSavingDetails(false);
  };

  const updateThirdPartyDraft = (id: string, field: keyof ThirdPartyForm, value: string) =>
    setThirdPartyDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const saveThirdPartyDraft = async (id: string) => {
    if (!selected || !canEdit) return;
    const draft = thirdPartyDrafts[id];
    if (!draft) return;
    setSavingThirdPartyId(id);
    try {
      const { key, ...fields } = draft;
      await saveThirdParty({ id, incident_id: selected.id, ...fields });
      await reloadSelectedThirdParties();
      toast({ title: "Saved", description: "Other party's details updated." });
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setSavingThirdPartyId(null);
  };

  const deleteThirdPartyRow = async (id: string) => {
    if (!canEdit) return;
    if (!window.confirm("Remove this party from the incident?")) return;
    setSavingThirdPartyId(id);
    try {
      await deleteThirdParty(id);
      await reloadSelectedThirdParties();
      toast({ title: "Removed", description: "Party removed from the incident." });
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setSavingThirdPartyId(null);
  };

  const addNewThirdPartyDraft = () => setAddedThirdParties((prev) => [...prev, emptyThirdParty()]);
  const removeNewThirdPartyDraft = (key: string) => setAddedThirdParties((prev) => prev.filter((p) => p.key !== key));
  const updateNewThirdPartyDraft = (key: string, field: keyof ThirdPartyForm, value: string) =>
    setAddedThirdParties((prev) => prev.map((p) => (p.key === key ? { ...p, [field]: value } : p)));

  const saveNewThirdPartyDraft = async (key: string) => {
    if (!selected || !canEdit) return;
    const draft = addedThirdParties.find((p) => p.key === key);
    if (!draft) return;
    setSavingThirdPartyId(key);
    try {
      const { key: _key, ...fields } = draft;
      await saveThirdParty({ incident_id: selected.id, party_type: "vehicle", ...fields });
      setAddedThirdParties((prev) => prev.filter((p) => p.key !== key));
      await reloadSelectedThirdParties();
      toast({ title: "Added", description: "Other party added to the incident." });
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setSavingThirdPartyId(null);
  };

  // Persists the full marker set for whichever view is currently active --
  // DamageMarkerOverlay hands back the complete post-edit array each time
  // (add or remove), so a full replace of just that view is simplest and
  // correct for this low-frequency, few-clicks interaction.
  const saveDiagramMarkers = async (view: DamageDiagramView, markers: DamageMarker[]) => {
    if (!selected || !canEdit) return;
    setSavingMarkers(true);
    try {
      await replaceIncidentDamageMarkersForView(
        selected.id,
        view,
        markers.map((m) => ({
          x_percent: m.x_percent,
          y_percent: m.y_percent,
          damage_type: m.damage_type,
          severity: m.severity,
          notes: m.notes || null,
        }))
      );
      const refreshed = await fetchIncidentDamageMarkers(selected.id);
      setDamageMarkers(refreshed);
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setSavingMarkers(false);
  };

  // Same "full replace" approach as saveDiagramMarkers, scoped to one third
  // party's diagram instead of our own vehicle's.
  const saveThirdPartyDiagramMarkers = async (thirdPartyId: string, view: DamageDiagramView, markers: DamageMarker[]) => {
    if (!selected || !canEdit) return;
    setSavingMarkers(true);
    try {
      await replaceIncidentDamageMarkersForView(
        selected.id,
        view,
        markers.map((m) => ({
          x_percent: m.x_percent,
          y_percent: m.y_percent,
          damage_type: m.damage_type,
          severity: m.severity,
          notes: m.notes || null,
        })),
        thirdPartyId
      );
      const refreshed = await fetchIncidentDamageMarkers(selected.id);
      setDamageMarkers(refreshed);
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setSavingMarkers(false);
  };

  const updateThirdPartyVehicleType = async (thirdPartyId: string, vehicleType: string) => {
    if (!selected || !canEdit) return;
    setSavingThirdPartyType(thirdPartyId);
    try {
      await saveThirdParty({ id: thirdPartyId, incident_id: selected.id, overlay_vehicle_type: vehicleType });
      const refreshed = await fetchIncidentThirdParties(selected.id);
      setSelectedThirdParties(refreshed);
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setSavingThirdPartyType(null);
  };

  // Lets photos be added to an already-logged incident -- needed e.g. after
  // a partial save (see addIncident's catch) where the incident record
  // exists but its photos never made it in.
  const uploadPhotosToSelected = async (files: FileList | null) => {
    if (!files || files.length === 0 || !selected || !canEdit) return;
    setUploadingSelectedPhoto(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const path = `incidents/${selected.id}/admin_${Date.now()}_${i}_${file.name}`;
        const { error: uploadError } = await supabase.storage.from("incident-reports").upload(path, file, {
          contentType: file.type || "image/jpeg",
          upsert: true,
        });
        if (uploadError) throw uploadError;
        const photo = await addIncidentPhoto({
          incident_id: selected.id,
          category: "scene",
          storage_path: path,
          latitude: null,
          longitude: null,
          taken_at: new Date().toISOString(),
          uploaded_by: "admin",
        });
        setSelectedPhotos((prev) => [...prev, photo]);
      }
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setUploadingSelectedPhoto(false);
  };

  const deleteSelectedIncident = async () => {
    if (!selected || !canEdit) return;
    if (!window.confirm(`Delete this incident (${selected.driver_name || "unknown driver"} -- ${formatDate(selected.incident_date)})? This can't be undone.`)) return;
    setDeleting(true);
    try {
      if (selectedPhotos.length > 0) {
        await supabase.storage.from("incident-reports").remove(selectedPhotos.map((p) => p.storage_path));
      }
      // Third parties and any linked claim cascade-delete with the incident
      // (see migration 0045/0047 foreign keys).
      await deleteIncident(selected.id);
      toast({ title: "Deleted", description: "Incident removed." });
      setSelected(null);
      load();
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setDeleting(false);
  };

  const saveFaultClassification = async () => {
    if (!selected || !canEdit) return;
    setSaving(true);
    try {
      const patch = {
        fault_classification: (faultClassification || null) as FaultClassification | null,
        repair_cost: faultClassification === "section2_with_repair_cost" && repairCost ? parseFloat(repairCost) : null,
        repairer_name: repairerName || null,
        repairer_address: repairerAddress || null,
        repairer_phone: repairerPhone || null,
        repairer_email: repairerEmail || null,
        repair_estimate: repairEstimate ? parseFloat(repairEstimate) : null,
        status: "reviewed" as IncidentStatus,
        reviewed_at: new Date().toISOString(),
      };
      await updateIncident(selected.id, patch);
      toast({ title: "Saved", description: "Fault classification updated -- see the status badge above." });
      // Keep the dialog open and reflect the save locally (instead of
      // closing) -- closing immediately after save is exactly why this
      // looked like nothing happened: the status badge flip was never
      // visible before the dialog vanished.
      setSelected((prev) => (prev ? { ...prev, ...patch } : prev));
      load();
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setSaving(false);
  };

  const calculateExcessImpact = async () => {
    if (!selected?.driver_name) return;
    setCalculating(true);
    try {
      const settings = await loadInsuranceExcessSettings();
      setExcessSettings(settings);
      const driver = drivers.find((d) => d.driver_name === selected.driver_name);
      const ledger = await loadDriverFundLedger(selected.driver_name);
      const status = calculateFundStatus(
        { insurance_start_date: driver?.insurance_start_date ?? null, insurance_last_claim_date: driver?.insurance_last_claim_date ?? null },
        ledger,
        settings
      );
      setFundPreview(status);
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setCalculating(false);
  };

  // Shows the driver's fund stage the moment an incident is opened, not just
  // after picking a chargeable fault classification -- so it's visible
  // context for making that classification decision, not just a result of it.
  useEffect(() => {
    if (selected?.driver_name) {
      calculateExcessImpact();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-xl text-primary flex items-center gap-2">Incidents</CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)} className="golden-button">
            <Plus className="w-4 h-4 mr-2" />
            Log Incident
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : incidents.length === 0 ? (
          <p className="text-sm text-muted-foreground">No incidents logged yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Driver</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Fault</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Claim</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {incidents.map((inc) => {
                  const claim = claims.find((c) => c.incident_id === inc.id);
                  return (
                    <TableRow key={inc.id} className="cursor-pointer hover:bg-yellow-900/10" onClick={() => openDetail(inc)}>
                      <TableCell>{formatDate(inc.incident_date)}</TableCell>
                      <TableCell className="font-medium">{inc.driver_name || "--"}</TableCell>
                      <TableCell>{inc.vehicle_number || "--"}</TableCell>
                      <TableCell>{CATEGORY_LABEL[inc.incident_category]}</TableCell>
                      <TableCell>{inc.fault_classification ? FAULT_LABEL[inc.fault_classification] : "--"}</TableCell>
                      <TableCell>
                        <Badge className={STATUS_BADGE[inc.status]}>{inc.status.replace(/_/g, " ")}</Badge>
                      </TableCell>
                      <TableCell>
                        {claim ? (
                          <Badge className="bg-yellow-800 text-yellow-200">{claim.status.replace(/_/g, " ")}</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">--</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Log Incident dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Log Incident</DialogTitle>
            <DialogDescription>Record what happened -- fault classification and excess impact are set separately once reviewed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Driver *</Label>
                <Select value={newForm.driver_name} onValueChange={(v) => setNewForm((f) => ({ ...f, driver_name: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select driver" />
                  </SelectTrigger>
                  <SelectContent>
                    {drivers.map((d) => (
                      <SelectItem key={d.driver_name} value={d.driver_name}>
                        {d.driver_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vehicle</Label>
                <Select value={newForm.vehicle_number} onValueChange={(v) => setNewForm((f) => ({ ...f, vehicle_number: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles
                      .filter((v) => v.status === "active" || !v.status)
                      .map((v) => (
                      <SelectItem key={v.vehicle_number} value={v.vehicle_number}>
                        #{v.vehicle_number} {v.registration ? `(${v.registration})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Incident Type *</Label>
                <Select value={newForm.incident_category} onValueChange={(v) => setNewForm((f) => ({ ...f, incident_category: v as IncidentCategory }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(CATEGORY_LABEL) as IncidentCategory[]).map((c) => (
                      <SelectItem key={c} value={c}>
                        {CATEGORY_LABEL[c]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input type="date" value={newForm.incident_date} onChange={(e) => setNewForm((f) => ({ ...f, incident_date: e.target.value }))} />
              </div>
              <div>
                <Label>Time</Label>
                <Input type="time" value={newForm.incident_time} onChange={(e) => setNewForm((f) => ({ ...f, incident_time: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Location</Label>
              <Input value={newForm.location_description} onChange={(e) => setNewForm((f) => ({ ...f, location_description: e.target.value }))} placeholder="e.g. Woolworths Broadway Carpark" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Weather</Label>
                <Input value={newForm.weather_conditions} onChange={(e) => setNewForm((f) => ({ ...f, weather_conditions: e.target.value }))} placeholder="e.g. Raining" />
              </div>
              <div>
                <Label>Road Conditions</Label>
                <Input value={newForm.road_conditions} onChange={(e) => setNewForm((f) => ({ ...f, road_conditions: e.target.value }))} placeholder="e.g. Wet, Sealed" />
              </div>
            </div>
            <div>
              <Label>What happened</Label>
              <Textarea value={newForm.narrative} onChange={(e) => setNewForm((f) => ({ ...f, narrative: e.target.value }))} rows={3} />
            </div>
            <div>
              <Label>Damage to our vehicle</Label>
              <Textarea value={newForm.own_vehicle_damage_description} onChange={(e) => setNewForm((f) => ({ ...f, own_vehicle_damage_description: e.target.value }))} rows={2} />
            </div>
            <div>
              <Label>Vehicle's Current Location</Label>
              <Input
                value={newForm.vehicle_location_now}
                onChange={(e) => setNewForm((f) => ({ ...f, vehicle_location_now: e.target.value }))}
                placeholder="e.g. At base, towed to repairer, still with driver"
              />
            </div>
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <Checkbox checked={newForm.police_involved} onCheckedChange={(v) => setNewForm((f) => ({ ...f, police_involved: !!v }))} />
                <Label className="text-sm">Police involved</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={newForm.witnesses_present} onCheckedChange={(v) => setNewForm((f) => ({ ...f, witnesses_present: !!v }))} />
                <Label className="text-sm">Witnesses</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={newForm.injuries} onCheckedChange={(v) => setNewForm((f) => ({ ...f, injuries: !!v }))} />
                <Label className="text-sm">Injuries</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={newForm.alcohol_drugs_involved} onCheckedChange={(v) => setNewForm((f) => ({ ...f, alcohol_drugs_involved: !!v }))} />
                <Label className="text-sm">Alcohol/drugs involved</Label>
              </div>
            </div>
            {newForm.police_involved && (
              <div>
                <Label className="text-xs">Police Incident Number</Label>
                <Input value={newForm.police_incident_number} onChange={(e) => setNewForm((f) => ({ ...f, police_incident_number: e.target.value }))} />
              </div>
            )}
            {newForm.witnesses_present && (
              <div>
                <Label className="text-xs">Witness Details</Label>
                <Textarea rows={2} value={newForm.witness_details} onChange={(e) => setNewForm((f) => ({ ...f, witness_details: e.target.value }))} placeholder="Name(s) and contact details" />
              </div>
            )}
            {newForm.injuries && (
              <div>
                <Label className="text-xs">Injury Details</Label>
                <Textarea rows={2} value={newForm.injury_details} onChange={(e) => setNewForm((f) => ({ ...f, injury_details: e.target.value }))} />
              </div>
            )}

            <div className="border-t border-border pt-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Other Vehicle(s) / Party Involved</Label>
                <Button type="button" variant="outline" size="sm" onClick={addThirdParty}>
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Add Party
                </Button>
              </div>
              {newThirdParties.map((p, idx) => (
                <div key={p.key} className="rounded-lg border border-border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-yellow-500">Party #{idx + 1}</p>
                    <Button variant="ghost" size="sm" onClick={() => removeThirdParty(p.key)} className="text-red-400 h-7 px-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Their Name</Label>
                      <Input value={p.other_driver_name} onChange={(e) => updateThirdParty(p.key, "other_driver_name", e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Their Phone</Label>
                      <Input value={p.other_driver_phone} onChange={(e) => updateThirdParty(p.key, "other_driver_phone", e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Their Licence Number</Label>
                      <Input value={p.other_driver_licence} onChange={(e) => updateThirdParty(p.key, "other_driver_licence", e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Vehicle Plate</Label>
                      <Input value={p.other_vehicle_rego} onChange={(e) => updateThirdParty(p.key, "other_vehicle_rego", e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Vehicle Make</Label>
                      <Input value={p.other_vehicle_make} onChange={(e) => updateThirdParty(p.key, "other_vehicle_make", e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Vehicle Model</Label>
                      <Input value={p.other_vehicle_model} onChange={(e) => updateThirdParty(p.key, "other_vehicle_model", e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Their Insurer (if known)</Label>
                      <Input value={p.other_insurer_name} onChange={(e) => updateThirdParty(p.key, "other_insurer_name", e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Their Claim/Policy Number</Label>
                      <Input value={p.other_claim_policy_number} onChange={(e) => updateThirdParty(p.key, "other_claim_policy_number", e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Owner's Name / Phone (if different from driver)</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={p.other_owner_name} onChange={(e) => updateThirdParty(p.key, "other_owner_name", e.target.value)} placeholder="Name" />
                      <Input value={p.other_owner_phone} onChange={(e) => updateThirdParty(p.key, "other_owner_phone", e.target.value)} placeholder="Phone" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Damage to their vehicle</Label>
                    <Textarea
                      rows={2}
                      value={p.other_vehicle_damage_description}
                      onChange={(e) => updateThirdParty(p.key, "other_vehicle_damage_description", e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-border pt-3 space-y-2">
              <Label className="text-sm font-semibold">Photos</Label>
              <div className="flex flex-wrap gap-2">
                {newPhotos.map((photo) => (
                  <div key={photo.key} className="relative w-20 h-20 rounded-md overflow-hidden border border-border">
                    <img src={photo.previewUrl} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(photo.key)}
                      className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <label className="w-20 h-20 rounded-md border border-dashed border-border flex items-center justify-center cursor-pointer hover:border-yellow-500/60">
                  <Camera className="w-6 h-6 text-muted-foreground" />
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => addPhotoFiles(e.target.files)} />
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setShowAdd(false);
                clearNewForm();
              }}
            >
              Cancel
            </Button>
            <Button variant="outline" onClick={clearNewForm} disabled={saving}>
              Clear Form
            </Button>
            <Button onClick={addIncident} disabled={saving} className="golden-button">
              Log Incident
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / review dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selected.driver_name} -- {formatDate(selected.incident_date)}
                  <Badge className={STATUS_BADGE[selected.status]}>{selected.status.replace(/_/g, " ")}</Badge>
                </DialogTitle>
                <DialogDescription>
                  {CATEGORY_LABEL[selected.incident_category]}
                  {selected.vehicle_number ? ` -- Vehicle #${selected.vehicle_number}` : ""}
                </DialogDescription>
              </DialogHeader>
              {(() => {
                const claim = claims.find((c) => c.incident_id === selected.id);
                return claim ? (
                  <div className="rounded-md border border-yellow-900/30 bg-yellow-950/10 px-3 py-2 text-xs text-yellow-200">
                    Already moved to a claim ({claim.status.replace(/_/g, " ")}) -- open it from the Claims tab to review, attach documents, or download the PDF.
                  </div>
                ) : null;
              })()}
              <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
                {selected.driver_name && (
                  <div className="rounded-lg border border-border bg-background/40 p-3 flex items-center gap-2 text-xs">
                    <DollarSign className="w-4 h-4 text-yellow-500 shrink-0" />
                    {calculating && !fundPreview ? (
                      <span className="text-muted-foreground">Checking excess fund status...</span>
                    ) : fundPreview ? (
                      <span className="text-foreground">
                        <span className="font-semibold">{selected.driver_name}</span> has been contributing for{" "}
                        <span className="font-semibold">{fundPreview.monthsContributing ?? "0"} month{fundPreview.monthsContributing === 1 ? "" : "s"}</span>
                        {" -- "}
                        {fundPreview.eligibleForClaim ? (
                          <>currently eligible to claim up to <span className="font-semibold text-yellow-400">${fundPreview.claimableTier.toFixed(2)}</span></>
                        ) : (
                          <>not yet eligible to claim (needs {excessSettings.minMonthsBeforeClaim} months)</>
                        )}
                        {fundPreview.eligibleForBonus && <span className="text-green-400"> -- eligible for the ${excessSettings.noClaimBonusAmount} No Claim Rebate</span>}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">No excess fund status available for this driver.</span>
                    )}
                  </div>
                )}
                <div className="border-t border-border pt-3 space-y-3">
                  <Label className="text-sm font-semibold">Incident Details</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Driver</Label>
                      <Input value={editForm.driver_name} disabled />
                    </div>
                    <div>
                      <Label className="text-xs">Vehicle</Label>
                      <Select value={editForm.vehicle_number} onValueChange={(v) => setEditForm((f) => ({ ...f, vehicle_number: v }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select vehicle..." />
                        </SelectTrigger>
                        <SelectContent>
                          {vehicles.map((v) => (
                            <SelectItem key={v.vehicle_number} value={v.vehicle_number}>
                              #{v.vehicle_number} {v.registration ? `(${v.registration})` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Incident Type</Label>
                      <Select value={editForm.incident_category} onValueChange={(v) => setEditForm((f) => ({ ...f, incident_category: v as IncidentCategory }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(Object.keys(CATEGORY_LABEL) as IncidentCategory[]).map((c) => (
                            <SelectItem key={c} value={c}>
                              {CATEGORY_LABEL[c]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Date</Label>
                      <Input type="date" value={editForm.incident_date} onChange={(e) => setEditForm((f) => ({ ...f, incident_date: e.target.value }))} />
                    </div>
                    <div>
                      <Label className="text-xs">Time</Label>
                      <Input type="time" value={editForm.incident_time} onChange={(e) => setEditForm((f) => ({ ...f, incident_time: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Location</Label>
                    <Input value={editForm.location_description} onChange={(e) => setEditForm((f) => ({ ...f, location_description: e.target.value }))} placeholder="e.g. Woolworths Broadway Carpark" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Weather</Label>
                      <Input value={editForm.weather_conditions} onChange={(e) => setEditForm((f) => ({ ...f, weather_conditions: e.target.value }))} placeholder="e.g. Raining" />
                    </div>
                    <div>
                      <Label className="text-xs">Road Conditions</Label>
                      <Input value={editForm.road_conditions} onChange={(e) => setEditForm((f) => ({ ...f, road_conditions: e.target.value }))} placeholder="e.g. Wet, Sealed" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">What happened</Label>
                    <Textarea rows={3} value={editForm.narrative} onChange={(e) => setEditForm((f) => ({ ...f, narrative: e.target.value }))} />
                  </div>
                  {selected.voice_transcript && (
                    <div className="rounded-md border border-border bg-background/40 p-2.5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Mic className="w-3.5 h-3.5 text-yellow-500" />
                        <Label className="text-xs font-semibold">Voice Report Transcript</Label>
                      </div>
                      <p className="text-xs text-muted-foreground mb-1.5">
                        What the driver actually said when they talked this report in, in case it's worth checking against the fields above.
                      </p>
                      <pre className="text-xs whitespace-pre-wrap font-sans text-foreground/90 max-h-[240px] overflow-y-auto">
                        {selected.voice_transcript}
                      </pre>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs">Damage to our vehicle</Label>
                    <Textarea rows={2} value={editForm.own_vehicle_damage_description} onChange={(e) => setEditForm((f) => ({ ...f, own_vehicle_damage_description: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-xs">Vehicle's Current Location</Label>
                    <Input
                      value={editForm.vehicle_location_now}
                      onChange={(e) => setEditForm((f) => ({ ...f, vehicle_location_now: e.target.value }))}
                      placeholder="e.g. At base, towed to repairer, still with driver"
                    />
                  </div>
                  <div className="flex items-center gap-6 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={editForm.police_involved} onCheckedChange={(v) => setEditForm((f) => ({ ...f, police_involved: !!v }))} />
                      <Label className="text-sm">Police involved</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox checked={editForm.witnesses_present} onCheckedChange={(v) => setEditForm((f) => ({ ...f, witnesses_present: !!v }))} />
                      <Label className="text-sm">Witnesses</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox checked={editForm.injuries} onCheckedChange={(v) => setEditForm((f) => ({ ...f, injuries: !!v }))} />
                      <Label className="text-sm">Injuries</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox checked={editForm.alcohol_drugs_involved} onCheckedChange={(v) => setEditForm((f) => ({ ...f, alcohol_drugs_involved: !!v }))} />
                      <Label className="text-sm">Alcohol/drugs involved</Label>
                    </div>
                  </div>
                  {editForm.police_involved && (
                    <div>
                      <Label className="text-xs">Police Incident Number</Label>
                      <Input value={editForm.police_incident_number} onChange={(e) => setEditForm((f) => ({ ...f, police_incident_number: e.target.value }))} />
                    </div>
                  )}
                  {editForm.witnesses_present && (
                    <div>
                      <Label className="text-xs">Witness Details</Label>
                      <Textarea rows={2} value={editForm.witness_details} onChange={(e) => setEditForm((f) => ({ ...f, witness_details: e.target.value }))} placeholder="Name(s) and contact details" />
                    </div>
                  )}
                  {editForm.injuries && (
                    <div>
                      <Label className="text-xs">Injury Details</Label>
                      <Textarea rows={2} value={editForm.injury_details} onChange={(e) => setEditForm((f) => ({ ...f, injury_details: e.target.value }))} />
                    </div>
                  )}
                  <Button variant="outline" size="sm" onClick={saveDetails} disabled={savingDetails}>
                    {savingDetails ? "Saving..." : "Save Details"}
                  </Button>
                </div>

                <div className="border-t border-border pt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Other Vehicle(s) / Party</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addNewThirdPartyDraft}>
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      Add Party
                    </Button>
                  </div>
                  {selectedThirdParties.map((p) => {
                    const draft = thirdPartyDrafts[p.id] || thirdPartyToForm(p);
                    const thirdPartyOverlayKey = p.overlay_vehicle_type
                      ? vehicleTypeOptions.find((t) => t.vehicle_type === p.overlay_vehicle_type)?.overlay_asset_key || null
                      : null;
                    const thirdPartyView = thirdPartyDiagramView[p.id] || "front";
                    const thirdPartyMarkers = damageMarkers.filter((m) => m.third_party_id === p.id);
                    const busy = savingThirdPartyId === p.id;
                    return (
                      <div key={p.id} className="rounded-lg border border-border p-3 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Their Name</Label>
                            <Input value={draft.other_driver_name} onChange={(e) => updateThirdPartyDraft(p.id, "other_driver_name", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-xs">Their Phone</Label>
                            <Input value={draft.other_driver_phone} onChange={(e) => updateThirdPartyDraft(p.id, "other_driver_phone", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-xs">Their Licence Number</Label>
                            <Input value={draft.other_driver_licence} onChange={(e) => updateThirdPartyDraft(p.id, "other_driver_licence", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-xs">Vehicle Plate</Label>
                            <Input value={draft.other_vehicle_rego} onChange={(e) => updateThirdPartyDraft(p.id, "other_vehicle_rego", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-xs">Vehicle Make</Label>
                            <Input value={draft.other_vehicle_make} onChange={(e) => updateThirdPartyDraft(p.id, "other_vehicle_make", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-xs">Vehicle Model</Label>
                            <Input value={draft.other_vehicle_model} onChange={(e) => updateThirdPartyDraft(p.id, "other_vehicle_model", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-xs">Their Insurer (if known)</Label>
                            <Input value={draft.other_insurer_name} onChange={(e) => updateThirdPartyDraft(p.id, "other_insurer_name", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-xs">Their Claim/Policy Number</Label>
                            <Input value={draft.other_claim_policy_number} onChange={(e) => updateThirdPartyDraft(p.id, "other_claim_policy_number", e.target.value)} />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Owner's Name / Phone (if different from driver)</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <Input value={draft.other_owner_name} onChange={(e) => updateThirdPartyDraft(p.id, "other_owner_name", e.target.value)} placeholder="Name" />
                            <Input value={draft.other_owner_phone} onChange={(e) => updateThirdPartyDraft(p.id, "other_owner_phone", e.target.value)} placeholder="Phone" />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Damage to their vehicle</Label>
                          <Textarea rows={2} value={draft.other_vehicle_damage_description} onChange={(e) => updateThirdPartyDraft(p.id, "other_vehicle_damage_description", e.target.value)} />
                        </div>
                        <div className="flex gap-2">
                          <Button type="button" size="sm" variant="outline" onClick={() => saveThirdPartyDraft(p.id)} disabled={busy}>
                            {busy ? "Saving..." : "Save Party"}
                          </Button>
                          <Button type="button" size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => deleteThirdPartyRow(p.id)} disabled={busy}>
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            Remove
                          </Button>
                        </div>

                        {p.party_type === "vehicle" && (
                          <div className="border-t border-border pt-2 space-y-2">
                            <div className="flex items-center gap-2">
                              <Label className="text-xs shrink-0">Vehicle Type (for damage diagram)</Label>
                              <Select
                                value={p.overlay_vehicle_type || ""}
                                onValueChange={(v) => updateThirdPartyVehicleType(p.id, v)}
                                disabled={savingThirdPartyType === p.id}
                              >
                                <SelectTrigger className="h-7 text-xs w-40">
                                  <SelectValue placeholder="Select type..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {vehicleTypeOptions
                                    .filter((t) => t.overlay_asset_key)
                                    .map((t) => (
                                      <SelectItem key={t.vehicle_type} value={t.vehicle_type}>
                                        {t.vehicle_type}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {thirdPartyOverlayKey && (
                              <div className="space-y-2">
                                <div className="flex gap-1.5 flex-wrap">
                                  {DAMAGE_DIAGRAM_VIEWS.map(({ view, label }) => {
                                    const count = thirdPartyMarkers.filter((m) => m.view === view).length;
                                    return (
                                      <Button
                                        key={view}
                                        type="button"
                                        size="sm"
                                        variant={thirdPartyView === view ? "secondary" : "outline"}
                                        onClick={() => setThirdPartyDiagramView((prev) => ({ ...prev, [p.id]: view }))}
                                      >
                                        {label}
                                        {count > 0 ? ` (${count})` : ""}
                                      </Button>
                                    );
                                  })}
                                </div>
                                <DamageMarkerOverlay
                                  imageUrl={overlayPublicUrl(thirdPartyOverlayKey, thirdPartyView)}
                                  photoType={thirdPartyView}
                                  markers={thirdPartyMarkers.filter((m) => m.view === thirdPartyView)}
                                  onMarkersChange={(markers) => saveThirdPartyDiagramMarkers(p.id, thirdPartyView, markers)}
                                  readOnly={savingMarkers}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {addedThirdParties.map((p, idx) => {
                    const busy = savingThirdPartyId === p.key;
                    return (
                      <div key={p.key} className="rounded-lg border border-yellow-900/40 bg-yellow-950/5 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-yellow-500">New Party #{idx + 1} (not yet saved)</p>
                          <Button variant="ghost" size="sm" onClick={() => removeNewThirdPartyDraft(p.key)} className="text-red-400 h-7 px-2">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Their Name</Label>
                            <Input value={p.other_driver_name} onChange={(e) => updateNewThirdPartyDraft(p.key, "other_driver_name", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-xs">Their Phone</Label>
                            <Input value={p.other_driver_phone} onChange={(e) => updateNewThirdPartyDraft(p.key, "other_driver_phone", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-xs">Their Licence Number</Label>
                            <Input value={p.other_driver_licence} onChange={(e) => updateNewThirdPartyDraft(p.key, "other_driver_licence", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-xs">Vehicle Plate</Label>
                            <Input value={p.other_vehicle_rego} onChange={(e) => updateNewThirdPartyDraft(p.key, "other_vehicle_rego", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-xs">Vehicle Make</Label>
                            <Input value={p.other_vehicle_make} onChange={(e) => updateNewThirdPartyDraft(p.key, "other_vehicle_make", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-xs">Vehicle Model</Label>
                            <Input value={p.other_vehicle_model} onChange={(e) => updateNewThirdPartyDraft(p.key, "other_vehicle_model", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-xs">Their Insurer (if known)</Label>
                            <Input value={p.other_insurer_name} onChange={(e) => updateNewThirdPartyDraft(p.key, "other_insurer_name", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-xs">Their Claim/Policy Number</Label>
                            <Input value={p.other_claim_policy_number} onChange={(e) => updateNewThirdPartyDraft(p.key, "other_claim_policy_number", e.target.value)} />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Owner's Name / Phone (if different from driver)</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <Input value={p.other_owner_name} onChange={(e) => updateNewThirdPartyDraft(p.key, "other_owner_name", e.target.value)} placeholder="Name" />
                            <Input value={p.other_owner_phone} onChange={(e) => updateNewThirdPartyDraft(p.key, "other_owner_phone", e.target.value)} placeholder="Phone" />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Damage to their vehicle</Label>
                          <Textarea rows={2} value={p.other_vehicle_damage_description} onChange={(e) => updateNewThirdPartyDraft(p.key, "other_vehicle_damage_description", e.target.value)} />
                        </div>
                        <Button type="button" size="sm" onClick={() => saveNewThirdPartyDraft(p.key)} disabled={busy} className="golden-button">
                          {busy ? "Adding..." : "Save Party"}
                        </Button>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-border pt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Photos</Label>
                    <label
                      className={`inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs font-medium hover:border-yellow-500/60 ${
                        uploadingSelectedPhoto ? "opacity-60 pointer-events-none" : "cursor-pointer"
                      }`}
                    >
                      <Camera className="w-3.5 h-3.5" />
                      {uploadingSelectedPhoto ? "Uploading..." : "Add"}
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        disabled={uploadingSelectedPhoto}
                        onChange={(e) => uploadPhotosToSelected(e.target.files)}
                      />
                    </label>
                  </div>
                  {selectedPhotos.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No photos yet.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {selectedPhotos.map((photo) => (
                        <a
                          key={photo.id}
                          href={publicPhotoUrl(photo.storage_path)}
                          target="_blank"
                          rel="noreferrer"
                          className="block w-20 h-20 rounded-md overflow-hidden border border-border"
                        >
                          <img src={publicPhotoUrl(photo.storage_path)} alt="" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                {overlayAssetKey && (
                  <div className="border-t border-border pt-3 space-y-2">
                    <Label className="text-sm font-semibold">Damage Diagram</Label>
                    <div className="flex gap-1.5 flex-wrap">
                      {DAMAGE_DIAGRAM_VIEWS.map(({ view, label }) => {
                        const count = damageMarkers.filter((m) => m.view === view && !m.third_party_id).length;
                        return (
                          <Button
                            key={view}
                            type="button"
                            size="sm"
                            variant={activeDiagramView === view ? "secondary" : "outline"}
                            onClick={() => setActiveDiagramView(view)}
                          >
                            {label}
                            {count > 0 ? ` (${count})` : ""}
                          </Button>
                        );
                      })}
                    </div>
                    <DamageMarkerOverlay
                      imageUrl={overlayPublicUrl(overlayAssetKey, activeDiagramView)}
                      photoType={activeDiagramView}
                      markers={damageMarkers.filter((m) => m.view === activeDiagramView && !m.third_party_id)}
                      onMarkersChange={(markers) => saveDiagramMarkers(activeDiagramView, markers)}
                      readOnly={savingMarkers}
                    />
                  </div>
                )}

                <div className="border-t border-border pt-3 space-y-3">
                  <div>
                    <Label>Fault Classification</Label>
                    <Select value={faultClassification} onValueChange={(v) => setFaultClassification(v as FaultClassification)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Not yet classified" />
                      </SelectTrigger>
                      <SelectContent>
                        {(Object.keys(FAULT_LABEL) as FaultClassification[]).map((f) => (
                          <SelectItem key={f} value={f}>
                            {FAULT_LABEL[f]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {faultClassification === "section2_with_repair_cost" && (
                    <div>
                      <Label>Actual Repair Cost ($)</Label>
                      <Input type="number" value={repairCost} onChange={(e) => setRepairCost(e.target.value)} placeholder="e.g. 500" />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Repairer Name</Label>
                      <Input value={repairerName} onChange={(e) => setRepairerName(e.target.value)} placeholder="e.g. Collision Repair Centre" />
                    </div>
                    <div>
                      <Label className="text-xs">Repairer Phone</Label>
                      <Input value={repairerPhone} onChange={(e) => setRepairerPhone(e.target.value)} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Repairer Address</Label>
                      <Input value={repairerAddress} onChange={(e) => setRepairerAddress(e.target.value)} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Repairer Email</Label>
                      <Input
                        type="email"
                        value={repairerEmail}
                        onChange={(e) => setRepairerEmail(e.target.value)}
                        placeholder="Used by the Claims tab's Email Repair Agent button"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Repair Estimate ($)</Label>
                      <Input type="number" value={repairEstimate} onChange={(e) => setRepairEstimate(e.target.value)} placeholder="e.g. 3500" />
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={saveFaultClassification} disabled={saving || !faultClassification}>
                    Save Classification
                  </Button>
                </div>

                <IncidentResponsibilityPanel
                  key={selected.id}
                  incident={selected}
                  drivers={drivers}
                  canEdit={canEdit}
                  classificationLabel={selected.fault_classification ? FAULT_LABEL[selected.fault_classification] : null}
                  defaultDescription={`${selected.fault_classification ? FAULT_SHORT[selected.fault_classification] : "Incident"} - ${
                    selected.vehicle_number ? `#${selected.vehicle_number} ` : ""
                  }${CATEGORY_LABEL[selected.incident_category].toLowerCase()}, ${formatDate(selected.incident_date)}`}
                  onChanged={load}
                />
              </div>
              <DialogFooter>
                <Button variant="ghost" className="text-red-400 hover:text-red-300" onClick={deleteSelectedIncident} disabled={deleting}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  {deleting ? "Deleting..." : "Delete Incident"}
                </Button>
                <Button variant="ghost" onClick={() => setSelected(null)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default InsuranceIncidentsTab;
